import axios from 'axios';
import { authFor } from './sessionLink';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Talking to the recording endpoints, and nothing about MediaRecorder.
 *
 * Gordon, 22 October: calls recordable at the user's option; attendees can
 * get at their recordings and an administrator at all of them; a transcript;
 * and from the transcript a summary and a per-person to-do list. The capture
 * itself lives in components/recording/Recorder.js; everything here is an
 * ordinary axios request carrying the same credential as the rest of the
 * application - authFor() is the contractor's token when there is one, and
 * otherwise the interceptor in lib/apiClient attaches the Entra token.
 *
 * The contract these follow is routes/recordings.js (migration 065). Until
 * that migration has run the server answers 503 naming it, and `notReady`
 * recognises that answer so the pages can say so calmly rather than failing.
 */

/** authFor() plus extra headers, without losing either. */
const withHeaders = (extra) => {
  const base = authFor() || {};
  return { ...base, headers: { ...(base.headers || {}), ...extra } };
};

/* ------------------------------------------------------------ in the call */

/**
 * What this call knows about recording, read on joining: { active:
 * Recording | null (with pending: [{ key, name }] while 'asking'),
 * recordings: [...], settings: { recording_enabled, recording_consent,
 * storage_configured, transcription_configured } }. The settings decide
 * whether the Record button is shown at all, and whether it can be pressed.
 */
export const sessionRecordings = (sessionId) => axios
  .get(`${API_BASE}/sessions/${sessionId}/recordings`, authFor())
  .then((r) => r.data);

/**
 * Start one. Answers 201 { id, state: 'recording' | 'asking', pending }.
 * `language` is a hint for the transcriber (en, mt, it, de, fr); left out,
 * it detects the language itself. 403 { recording_enabled: false } when the
 * firm has it off, 409 { recording_id, state } when somebody else already
 * is, 503 naming the storage variables when the server has nowhere to put it.
 */
export const startRecording = (sessionId, { kind, mime, language = null }) => axios
  .post(`${API_BASE}/sessions/${sessionId}/recordings`,
    { kind, mime, ...(language ? { language } : {}) }, authFor())
  .then((r) => r.data);

/** Accept or decline being recorded ('ask' mode). Answers { state, pending }. */
export const answerConsent = (sessionId, recordingId, accept) => axios
  .post(`${API_BASE}/sessions/${sessionId}/recordings/${recordingId}/consent`,
    { accept: Boolean(accept) }, authFor())
  .then((r) => r.data);

/**
 * One chunk, raw bytes, numbered. The body is the Blob itself - not a form -
 * with the recording's mime as its Content-Type, so the server appends it to
 * the file without re-reading it. 204 with X-Next-Chunk; 409 { expected }
 * when a number is skipped (sending the last one again is taken as a retry);
 * 413 over 8 MB.
 */
export const putChunk = (sessionId, recordingId, seq, blob, mime) => axios
  .put(`${API_BASE}/sessions/${sessionId}/recordings/${recordingId}/chunks/${seq}`, blob,
    { ...withHeaders({ 'Content-Type': mime || 'application/octet-stream' }), timeout: 60000 })
  .then((r) => ({ next: Number(r.headers?.['x-next-chunk']) }));

/** The recording is over: how long it ran. */
export const finishRecording = (sessionId, recordingId, durationMs) => axios
  .post(`${API_BASE}/sessions/${sessionId}/recordings/${recordingId}/finish`,
    { duration_ms: Math.max(0, Math.round(durationMs || 0)) }, authFor())
  .then((r) => r.data);

/* ------------------------------------------------------------ in the room
 *
 * A meeting of kind 'room' - everybody around one table, one phone or laptop
 * recording - has no call session, so these address the recording by its own
 * id rather than through /sessions/:id. The chunk and finish calls are the
 * same shape as the in-call ones above; pause and resume are new, because a
 * meeting has a coffee break and a call does not. The server's side of this
 * is migration 066; `roomNotReady` recognises the 503 before it has run.
 */

/**
 * Start recording a room meeting. 201 { id, state: 'recording', ... }.
 * `attendees` is the list of names the organiser confirmed were in the room,
 * which is what the speaker-naming picker offers afterwards; `language` is
 * the transcriber's hint, as in a call.
 */
/*
 * A CALL ON ANOTHER APP (090) adds two things: `consentConfirmed`, that the
 * person recording has told the other side (the server refuses a call's
 * recording without it, 409 consent_required), and `capture` - 'mic',
 * 'mic+system' or 'mic+tab' - what this browser is mixing, so the transcript's
 * reader knows whether the other side could be in it.
 */
export const startMeetingRecording = (meetingId, { kind = 'audio', mime, language = null, attendees = null, consentConfirmed = null, capture = null }) => axios
  .post(`${API_BASE}/meetings/${meetingId}/recordings`,
    { kind, mime, ...(language ? { language } : {}), ...(Array.isArray(attendees) ? { attendees } : {}),
      ...(consentConfirmed ? { consent_confirmed: true } : {}), ...(capture ? { capture } : {}) }, authFor())
  .then((r) => r.data);

/** What the browser mixed, in words, for a recording of a call on another app. */
export const captureWords = (capture) => (capture === 'mic+system' ? 'Your microphone and the computer’s sound'
  : capture === 'mic+tab' ? 'Your microphone and the browser tab’s sound'
    : capture === 'mic' ? 'Your microphone only — the other side is not in this recording' : null);

/** One chunk of a room recording, raw bytes, numbered - PUT /recordings/:rid/chunks/:seq. */
export const putChunkById = (recordingId, seq, blob, mime) => axios
  .put(`${API_BASE}/recordings/${recordingId}/chunks/${seq}`, blob,
    { ...withHeaders({ 'Content-Type': mime || 'application/octet-stream' }), timeout: 60000 })
  .then((r) => ({ next: Number(r.headers?.['x-next-chunk']) }));

/** A break. Answers { state: 'paused', paused_ms }. */
export const pauseRecording = (recordingId) => axios
  .post(`${API_BASE}/recordings/${recordingId}/pause`, {}, authFor())
  .then((r) => r.data);

/** Back from the break. Answers { state: 'recording' }. */
export const resumeRecording = (recordingId) => axios
  .post(`${API_BASE}/recordings/${recordingId}/resume`, {}, authFor())
  .then((r) => r.data);

/** The meeting is over: how long was actually recorded, breaks excluded. */
export const finishById = (recordingId, durationMs) => axios
  .post(`${API_BASE}/recordings/${recordingId}/finish`,
    { duration_ms: Math.max(0, Math.round(durationMs || 0)) }, authFor())
  .then((r) => r.data);

/** The server has the code and not the meetings table yet: 503 naming migrate:066. */
export const roomNotReady = (err) => {
  if (err?.response?.status !== 503) return false;
  const body = err.response.data;
  const text = typeof body === 'string' ? body : JSON.stringify(body || '');
  return /migrate:06[56]|meetings|recordings/i.test(text);
};

/* --------------------------------------------------------- afterwards */

/** Mine, or all of them for an administrator; `q` narrows by text. */
export const listRecordings = ({ scope = 'mine', q = '' } = {}) => axios
  .get(`${API_BASE}/recordings`, { ...authFor(), params: { scope, ...(q ? { q } : {}) } })
  .then((r) => (Array.isArray(r.data) ? r.data
    : Array.isArray(r.data?.recordings) ? r.data.recordings : []));

export const getRecording = (id) => axios
  .get(`${API_BASE}/recordings/${id}`, authFor())
  .then((r) => r.data);

/**
 * THE REASON (3 Nov): somebody not on the call is answered 428
 * reason_required until they have said why, once a day per recording.
 * components/recording/useRecordingReason asks and calls this; the reason
 * goes in the body, never the address.
 */
export const needsRecordingReason = (err) => err?.response?.status === 428
  && (err.response.data?.code === 'reason_required' || err.response.data?.reason_required === true);

export const giveRecordingReason = (id, reason, what = 'transcript') => axios
  .post(`${API_BASE}/recordings/${id}/reason`, { reason, what }, authFor())
  .then((r) => r.data);

/**
 * The transcript saved as a file. The text is already on the page, so the
 * server cannot see the download; this tells it, for the Audit log - which
 * records it only when the person was not on the call. Best effort.
 */
export const noteDownload = (id) => axios
  .post(`${API_BASE}/recordings/${id}/downloaded`, {}, authFor())
  .catch(() => null);

/**
 * The bytes, fetched with the sign-in and handed back as a Blob for an object
 * URL - a <video src> carries no token and would be answered 401, the same
 * reason chat files are fetched (lib/chatLink chatFileBlob).
 */
export const mediaBlob = (id) => axios
  .get(`${API_BASE}/recordings/${id}/media`, { ...authFor(), responseType: 'blob', timeout: 0 })
  .then((r) => r.data)
  .catch(async (err) => {
    // Asked for a blob, the server's {error} arrives as a blob too. Read it
    // back into the shape every other call here fails with.
    const data = err.response?.data;
    if (data && typeof data.text === 'function') {
      try { err.response.data = JSON.parse(await data.text()); } catch { /* not JSON */ }
    }
    throw err;
  });

export const deleteRecording = (id) => axios
  .delete(`${API_BASE}/recordings/${id}`, authFor())
  .then((r) => r.data);

/** Keep it past the firm's retention, until a day ('YYYY-MM-DD'); null lets it go again. */
export const keepRecording = (id, until) => axios
  .post(`${API_BASE}/recordings/${id}/keep`, { until: until || null }, authFor())
  .then((r) => r.data);

/** { 'Speaker 2': 'Rita Gatt' } - applied to every segment, server-side too. */
export const renameSpeakers = (id, map) => axios
  .patch(`${API_BASE}/recordings/${id}/speakers`, { map }, authFor())
  .then((r) => r.data);

export const createSummary = (id, prompt) => axios
  .post(`${API_BASE}/recordings/${id}/summary`, prompt ? { prompt } : {}, authFor())
  .then((r) => r.data);

export const extractTodos = (id, prompt) => axios
  .post(`${API_BASE}/recordings/${id}/todos`, prompt ? { prompt } : {}, authFor())
  .then((r) => r.data);

/**
 * Make the list(s) from what was ticked. `mode` is the organiser's choice
 * (Gordon, 22 October): 'shared' is one list shared with everybody on the
 * call; 'personal' is a private list each, every person's items in their
 * own. Answers { mode, created, per_person, skipped, list_id? } - list_id
 * only when there is one list to open.
 */
export const createTodoList = (id, summaryId, selection, mode = 'shared') => axios
  .post(`${API_BASE}/recordings/${id}/todos/${summaryId}/create`, { selection, mode }, authFor())
  .then((r) => r.data);

/* ------------------------------------------------------------ reading errors */

/** The server has the code and not the table yet: 503 naming migrate:065. */
export const notReady = (err) => {
  if (err?.response?.status !== 503) return false;
  const body = err.response.data;
  const text = typeof body === 'string' ? body : JSON.stringify(body || '');
  return /migrate:065|recordings/i.test(text);
};

/**
 * The sentence to show for a failed call, from the server when it gave one -
 * including a model's refusal (422 { refused: true, reason }), which is a
 * reason worth reading rather than an error.
 */
export const reasonOf = (err, fallback) => {
  const d = err?.response?.data;
  if (d?.refused && d.reason) return `The model declined: ${d.reason}`;
  return d?.error || d?.detail || d?.reason || fallback;
};

/** "Transcription is not set up on this server" - a stored recording the server could not transcribe. */
export const transcriptTrouble = (rec) => {
  if (!rec?.transcript_error) return null;
  return /AZURE|configured|not set/i.test(String(rec.transcript_error))
    ? 'Transcription is not set up on this server, so this recording has no transcript. The recording itself can still be played.'
    : `The transcript could not be written: ${rec.transcript_error}`;
};

/* ------------------------------------------------------------ saying things */

/** 3:12, or 1:03:12 past the hour. */
export const clockOf = (ms) => {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
};

/** "12 min", "1 h 03 min" - a duration in a list rather than on a clock. */
export const spokenLength = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
};

/** "Kept", "Expires today", "Expires in 12 days", or null when it is not going anywhere. */
export const expiryOf = (rec, now = new Date()) => {
  if (!rec) return null;
  if (rec.keep_until) return { kind: 'kept', text: 'Kept' };
  if (!rec.expires_at) return null;
  const at = new Date(rec.expires_at);
  if (Number.isNaN(at.getTime())) return null;
  const days = Math.ceil((at - now) / 86400000);
  if (days <= 0) return { kind: 'soon', text: 'Expires today' };
  if (days === 1) return { kind: 'soon', text: 'Expires tomorrow' };
  return { kind: days <= 7 ? 'soon' : 'later', text: `Expires in ${days} days` };
};

/** The five transcript languages, said in full; anything else as it came. */
export const LANGUAGES = { en: 'English', mt: 'Malti', it: 'Italiano', de: 'Deutsch', fr: 'Français' };
export const languageOf = (code) => (code === 'mixed' ? 'Malti + English' : code === 'auto' ? 'Any of the five'
  : LANGUAGES[String(code || '').toLowerCase().slice(0, 2)] || (code ? String(code).toUpperCase() : null));

/**
 * What a recording can be transcribed in (23 October): Malti + English - the
 * firm's default unless Settings -> Rules says otherwise - any of the five,
 * or one language. `value` is what POST .../recordings takes as `language`.
 */
export const RECORDING_LANGUAGES = [
  { value: 'mixed', label: 'Malti + English' },
  { value: 'auto', label: 'Detect - any of the five' },
  ...Object.entries(LANGUAGES).map(([value, label]) => ({ value, label: `${label} only` }))
];
/** The firm's default as a sentence on the Record dialog. */
export const defaultLanguageWords = (v) => {
  const code = v === 'mixed' || v === 'auto' ? v : String(v || '').slice(0, 2).toLowerCase();
  return (RECORDING_LANGUAGES.find((l) => l.value === code) || RECORDING_LANGUAGES[0]).label;
};
/** Said under the choice, so nobody is surprised by the result or the bill. */
export const MIXED_HINT = 'Malti + English is one transcription at the usual cost: Azure decides phrase by phrase which of the two '
  + 'is being spoken. A switch in the middle of one sentence is written in that phrase\'s main language.';

/** "Transcribing…", "Ready", "Could not transcribe" - what a recording is at. */
export const transcriptWords = (rec) => {
  if (!rec) return '';
  if (rec.state === 'recording' || rec.state === 'asking') return 'Recording…';
  if (rec.state === 'declined') return 'Declined';
  if (rec.state === 'failed' || rec.transcript_state === 'failed') return 'Failed';
  // The transcript's own word first, where the server gives one; the
  // recording's state is the coarser answer.
  if (rec.transcript_state === 'ready' || rec.transcript_state === 'done') return 'Ready';
  if (['pending', 'running', 'queued', 'transcribing'].includes(rec.transcript_state)) return 'Transcribing…';
  if (rec.transcript_error || rec.transcript_state === 'none' || rec.transcript_state === 'skipped') return 'No transcript';
  if (rec.state === 'ready') return 'Ready';
  if (rec.state === 'uploading' || rec.state === 'transcribing') return 'Transcribing…';
  if (rec.state === 'stored') return 'Saved';
  return rec.transcript_state ? String(rec.transcript_state) : 'Saved';
};

/**
 * The transcript as text, for copying and for a .txt download:
 *   [03:12] Rita Gatt: the serial is on the underside
 */
export const transcriptText = (rec, segments) => {
  const head = [rec?.about?.label, rec?.started_at ? new Date(rec.started_at).toLocaleString('en-GB') : null]
    .filter(Boolean).join(' — ');
  const lines = (segments || []).map((s) => `[${clockOf(s.start_ms)}] ${s.speaker ? `${s.speaker}: ` : ''}${s.text || ''}`);
  return [head, '', ...lines].join('\n').trim() + '\n';
};
